<template>
    <div id="fiche-bu-tf1-progression-graph" class="background-quaternary-lighter flex flex-column"
        :class="{ 'fullscreen-graph': isFullscreen }">
        <div class="background-quaternary flex text-center">
            <div class="col-12 flex justify-content-center align-items-center" style="position: relative;">
                <as-title class="color-white" :title="$t('ficheBu.detail.tf1_progression_graph.title')" :size="4"
                    style="margin-bottom: 0;"></as-title>
                <Button id="fiche-bu-tf1-progression-graph-minimize-button" v-show="isFullscreen"
                    @click="isFullscreen = false" icon="pi pi-window-minimize"
                    class="p-button-sm p-button-rounded button-default reversed" style="margin-left:10px;" />
                <Button id="fiche-bu-tf1-progression-graph-maximize-button" v-show="!isFullscreen"
                    @click="isFullscreen = true" icon="pi pi-window-maximize"
                    class="p-button-sm p-button-rounded button-default reversed" style="margin-left:10px;" />
            </div>
        </div>
        <div class="flex" :style="{ height: isFullscreen ? 'calc(100vh - 70px)' : '450px' }">
            <ProgressBar v-if="isBusy" mode="indeterminate" style="height: 3px; max-width: 75%;" class="flex-1 m-auto">
            </ProgressBar>
            <div v-else-if="!graphTtf1ProgressionData || graphTtf1ProgressionData.length === 0"
                class="w-100 h-100 flex flex-column align-items-center text-center">
                <as-title class="mt-auto" :title="$t('ficheBu.detail.no_data_message')" :size="6"></as-title>
                <img :src="noDataImage" class="ml-auto mr-auto mb-auto" />
            </div>
            <div v-else class="w-100 flex-1">
                <div style="position: relative; height: 100%; width: 100%;">
                    <Scatter :key="isFullscreen ? 'fullscreen' : 'normal'" :data="chartData" :options="chartOptions"
                        :plugins="chartPlugins" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Scatter } from 'vue-chartjs';
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    LinearScale,
    PointElement,
    LineElement
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import ProgressBar from "primevue/progressbar";
import Button from "primevue/button";
import AsTitle from '../Elements/AsTitle.vue';

ChartJS.register(
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
    annotationPlugin
)

export default {
    name: "FicheBuTf1ProgressionGraph",
    components: { AsTitle, Scatter, ProgressBar, Button },
    props: {
        isBusy: Boolean,
        graphTtf1ProgressionData: {
            type: Array,
        },
        selectedEntrepriseId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            noDataImage: new URL("../../assets/images/icons/no-data-warning.png", import.meta.url).href,
            isFullscreen: false
        };
    },
    mounted() {
        window.addEventListener('keydown', (e) => {
            if (e && e.key === 'Escape') {
                this.isFullscreen = false
            }
        });
    },
    computed: {
        chartPlugins() {
            return [
                {
                    id: 'custom_canvas_background_color',
                    beforeDraw: (chart) => {
                        const { ctx, chartArea } = chart
                        if (!chartArea) return
                        ctx.save()
                        ctx.fillStyle = 'white'
                        ctx.fillRect(chartArea.left, chartArea.top, chartArea.width, chartArea.height)
                        ctx.restore()
                    }
                }
            ];
        },
        chartData() {
            if (!this.graphTtf1ProgressionData || this.graphTtf1ProgressionData.length === 0) {
                return { datasets: [] }
            }

            const dataPoints = this.graphTtf1ProgressionData.map(d => {
                let color = 'green'

                if (d.TF > 10.5) {
                    color = 'red'
                } else if (d.TFP > 0) {
                    color = 'orange'
                }

                const realProgression = Number(d.TFP?.toFixed(2) || 0)

                let visualX = realProgression
                if (visualX > 100) visualX = 100
                if (visualX < -100) visualX = -100

                return {
                    x: visualX,
                    realX: realProgression,
                    y: Number(d.TF?.toFixed(2) || 0),
                    buName: d.buCode,
                    buId: d.buId,
                    backgroundColor: color,
                    borderColor: color,
                    pointStyle: realProgression > 100 || realProgression < -100 ? 'triangle' : 'circle'
                };
            })

            const selectedId = String(this.selectedEntrepriseId)

            return {
                datasets: [
                    {
                        label: 'BUs',
                        data: dataPoints,
                        backgroundColor: dataPoints.map(p => p.backgroundColor),
                        borderColor: dataPoints.map(p => String(p.buId) === selectedId ? 'black' : p.borderColor),
                        borderWidth: dataPoints.map(p => String(p.buId) === selectedId ? 2 : 1),
                        pointRadius: dataPoints.map(p => String(p.buId) === selectedId ? 10 : 6),
                        pointHoverRadius: dataPoints.map(p => String(p.buId) === selectedId ? 12 : 8),
                        pointStyle: dataPoints.map(p => p.pointStyle)
                    }
                ]
            }
        },
        chartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                    padding: {
                        top: 25,
                        right: 25,
                        left: 10,
                        bottom: 10
                    }
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                        labels: {
                            usePointStyle: true,
                            generateLabels: (chart) => {
                                return [
                                    { text: 'BUs improving TF1 and under 10.5 target', fillStyle: 'green', strokeStyle: 'green', lineWidth: 0, hidden: false },
                                    { text: 'BUs worsening TF1 but under 10.5 target', fillStyle: 'orange', strokeStyle: 'orange', lineWidth: 0, hidden: false },
                                    { text: 'BUs above 10.5 target', fillStyle: 'red', strokeStyle: 'red', lineWidth: 0, hidden: false }
                                ];
                            }
                        }
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const pt = context.raw;
                                return `${pt.buName} : TF1 = ${pt.y} | Prog. = ${pt.realX}%`;
                            }
                        }
                    },
                    datalabels: {
                        display: true,
                        align: () => {
                            return 'top';
                        },
                        offset: 6,
                        anchor: 'center',
                        formatter: (value) => {
                            if (value.realX > 100 || value.realX < -100) {
                                return `${value.buName} (${value.realX}%)`;
                            }
                            return value.buName;
                        },
                        font: (context) => {
                            const pt = context.dataset.data[context.dataIndex];
                            const isSelected = String(pt.buId) === String(this.selectedEntrepriseId);
                            return {
                                size: isSelected ? 14 : 11,
                                weight: isSelected ? 'bold' : 'normal'
                            };
                        },
                        color: (context) => {
                            const pt = context.dataset.data[context.dataIndex];
                            const isSelected = String(pt.buId) === String(this.selectedEntrepriseId);
                            return isSelected ? 'black' : '#777';
                        },
                        borderRadius: 3,
                        padding: 2
                    },
                    annotation: {
                        drawTime: 'beforeDatasetsDraw',
                        annotations: {
                            boxRed: {
                                type: 'box',
                                yMin: 10.5,
                                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                                borderWidth: 0
                            },
                            boxGreen: {
                                type: 'box',
                                yMax: 10.5,
                                xMax: 0,
                                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                                borderWidth: 0
                            },
                            boxBeige: {
                                type: 'box',
                                yMax: 10.5,
                                xMin: 0,
                                backgroundColor: 'rgba(255, 235, 160, 0.3)',
                                borderWidth: 0
                            },
                            lineTarget: {
                                type: 'line',
                                yMin: 10.5,
                                yMax: 10.5,
                                borderColor: 'grey',
                                borderWidth: 1,
                                borderDash: [5, 5]
                            },
                            lineZero: {
                                type: 'line',
                                xMin: 0,
                                xMax: 0,
                                borderColor: 'grey',
                                borderWidth: 1,
                                borderDash: [5, 5]
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        type: 'linear',
                        position: 'bottom',
                        max: 100,
                        title: {
                            display: true,
                            text: 'Progression TF1 vs N-1 (%)'
                        },
                    },
                    y: {
                        type: 'linear',
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'TF1 12 months rolling'
                        },
                    }
                }
            }
        }
    },
};
</script>

<style lang="scss">
@import "../../assets/styles/colors.scss";

#fiche-bu-tf1-progression-graph.fullscreen-graph {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    height: 100vh;
    width: 100vw;
}

#fiche-bu-tf1-progression-graph-maximize-button {
    position: absolute;
    right: 3px;
}

#fiche-bu-tf1-progression-graph-minimize-button {
    position: fixed;
    right: 3px;
    z-index: 101;
}
</style>